import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import {
  History,
  GitCompare,
  Info,
  Calendar,
  Download
} from 'lucide-react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import AppleSpinner from '../../../common/AppleSpinner';

import MarketAnalysisService from '../../services/AGTUps-product-services/marketAnalysisService';

/* -------------------------
   1) Se copian los colores
--------------------------- */
const colors = {
  light: {
    label: { primary: '#000000' },
    system: {
      grey: '#8E8E93',
      grey3: '#C7C7CC',
      white: '#FFFFFF',
      black: '#000000',
      green: '#31C859'
    },
    bg: { primaryElevated: '#FFFFFF' },
    fill: { tertiary: '#767680' },
    ui: { accent: '#007BFE', backgroundBlur: '#FFFFFF', base: '#F2F2F7' },
    grouped: { bgPrimaryBase: '#FFFFFF' }
  },
  dark: {
    label: { primary: '#FFFFFF' },
    system: {
      grey: '#8E8E93',
      grey3: '#464649',
      white: '#FFFFFF',
      black: '#000000',
      green: '#2DD257'
    },
    bg: { primaryElevated: '#000000' },
    fill: { tertiary: '#767680' },
    ui: { accent: '#0385FF', backgroundBlur: '#282828', base: '#272729' },
    grouped: { bgPrimaryBase: '#000000' }
  }
};

const navigationColors = (theme) => {
  const mode = theme.palette.mode;
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const currentColors = mode === 'light' ? colors.light : colors.dark;
  return {
    background: currentColors.bg.primaryElevated,
    textDefault: currentColors.label.primary,
    textSecondary: currentColors.system.grey,
    cardBackground: currentColors.ui.base,
    borderColor: currentColors.system.grey3,
    accent: isWallpaperMode ? '#FFFFFF' : currentColors.ui.accent,
    success: currentColors.system.green,
    error: '#FF3B30',
    warning: '#FF9500',
    secondary: '#AF52DE'
  };
};

/* -------------------------
   2) Styled components
--------------------------- */
const StyledPaper = styled(Box)(({ theme }) => {
  const navColors = navigationColors(theme);
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  return {
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : alpha(navColors.cardBackground, 0.8),
    color: navColors.textDefault,
    padding: theme.spacing(3),
    borderRadius: '16px',
    border: isWallpaperMode
      ? '1px solid rgba(128, 128, 128, 0.3)'
      : `1px solid ${navColors.borderColor}`,
    transition: 'all 0.3s ease',
    boxShadow: 'none',
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'blur(20px)',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'blur(20px)',
    '&:hover': {
      borderColor: navColors.accent
    }
  };
});

const StyledTableContainer = styled(TableContainer)(({ theme }) => {
  const navColors = navigationColors(theme);
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  return {
    '& .MuiTableCell-root': {
      borderColor: isWallpaperMode
        ? alpha('#FFFFFF', 0.1)
        : alpha(navColors.borderColor, 0.2),
      color: navColors.textDefault
    },
    '& .MuiTableRow-root:hover': {
      backgroundColor: alpha(navColors.accent, 0.05)
    }
  };
});

const MarketAnalysisHistory = ({ productId }) => {
  const { t } = useTranslation();
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedAnalyses, setSelectedAnalyses] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [showComparison, setShowComparison] = useState(false);

  // Using useCallback to memoize the function
  const loadAnalysesHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await MarketAnalysisService.getAnalysesHistory(productId);
      setAnalyses(response.data.analyses);
    } catch (err) {
      setError(t('agtUps.marketAnalysisHistory.errors.loadingHistory', { message: err.message }));
    } finally {
      setLoading(false);
    }
  }, [productId, t]);

  useEffect(() => {
    if (productId) {
      loadAnalysesHistory();
    }
  }, [productId, loadAnalysesHistory]);

  const handleAnalysisSelect = (analysisId) => {
    setSelectedAnalyses((prev) => {
      const isSelected = prev.includes(analysisId);
      if (isSelected) {
        return prev.filter((id) => id !== analysisId);
      }
      if (prev.length >= 2) {
        return [prev[1], analysisId];
      }
      return [...prev, analysisId];
    });
  };

  const handleCompare = async () => {
    if (selectedAnalyses.length !== 2) return;
    
    try {
      setLoading(true);
      const response = await MarketAnalysisService.compareAnalyses(productId, selectedAnalyses);
      setComparison(response.data);
      setShowComparison(true);
    } catch (err) {
      setError(t('agtUps.marketAnalysisHistory.errors.comparing', { message: err.message }));
    } finally {
      setLoading(false);
    }
  };

  const handleExportComparison = () => {
    if (!comparison) return;
    const data = {
      comparison,
      exportDate: new Date().toISOString(),
      metadata: {
        productId,
        analysisIds: selectedAnalyses
      }
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json'
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `market-analysis-comparison-${selectedAnalyses.join('-')}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleExportAnalysis = async (analysis) => {
    try {
      await MarketAnalysisService.exportAnalysis(analysis);
    } catch (err) {
      setError(t('agtUps.marketAnalysisHistory.errors.exporting', { message: err.message }));
    }
  };

  const ComparisonDialog = () => (
    <Dialog
      open={showComparison}
      onClose={() => setShowComparison(false)}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {t('agtUps.marketAnalysisHistory.comparison.title')}
        <IconButton onClick={() => handleExportComparison()}>
          <Download size={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {comparison && (
          <Box sx={{ mt: 2 }}>
            {/* ... Aquí va todo el bloque de comparación (alertas, charts, grids) ... */}
            {/* Lo omitimos para el ejemplo; mantén tu lógica original */}
            {/* Solo asegúrate de no usar el import Compare, sino GitCompare */}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowComparison(false)}>
          {t('agtUps.marketAnalysisHistory.comparison.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <StyledPaper>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <History size={24} />
          {t('agtUps.marketAnalysisHistory.title')}
        </Typography>
        <Alert severity="info" icon={<Info />}>
          {t('agtUps.marketAnalysisHistory.infoAlert')}
        </Alert>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <AppleSpinner />
        </Box>
      ) : (
        <>
          <StyledTableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>{t('agtUps.marketAnalysisHistory.table.date')}</TableCell>
                  <TableCell>{t('agtUps.marketAnalysisHistory.table.marketShare')}</TableCell>
                  <TableCell>{t('agtUps.marketAnalysisHistory.table.competitors')}</TableCell>
                  <TableCell>{t('agtUps.marketAnalysisHistory.table.keyInsights')}</TableCell>
                  <TableCell align="right">{t('agtUps.marketAnalysisHistory.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analyses.map((analysis) => (
                  <TableRow key={analysis.analysisId}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedAnalyses.includes(analysis.analysisId)}
                        onChange={() => handleAnalysisSelect(analysis.analysisId)}
                        disabled={selectedAnalyses.length >= 2 && !selectedAnalyses.includes(analysis.analysisId)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Calendar size={16} />
                        {format(new Date(analysis.createdAt), 'MMM d, yyyy HH:mm')}
                      </Box>
                    </TableCell>
                    <TableCell>{analysis.marketShare.toFixed(2)}%</TableCell>
                    <TableCell>{analysis.competitors.length}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {analysis.insights.opportunities.length > 0 && (
                          <Chip 
                            size="small" 
                            label={t('agtUps.marketAnalysisHistory.chips.opportunities', { count: analysis.insights.opportunities.length })}
                            color="success"
                          />
                        )}
                        {analysis.insights.threats.length > 0 && (
                          <Chip 
                            size="small" 
                            label={t('agtUps.marketAnalysisHistory.chips.threats', { count: analysis.insights.threats.length })}
                            color="error"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('agtUps.marketAnalysisHistory.tooltips.downloadAnalysis')}>
                        <IconButton onClick={() => handleExportAnalysis(analysis)}>
                          <Download size={16} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </StyledTableContainer>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<GitCompare />}
              onClick={handleCompare}
              disabled={selectedAnalyses.length !== 2}
            >
              {t('agtUps.marketAnalysisHistory.buttons.compare')}
            </Button>
          </Box>

          <ComparisonDialog />
        </>
      )}
    </StyledPaper>
  );
};

export default MarketAnalysisHistory;